import React from 'react';
import styled from 'styled-components';

import { toTitleCase } from '../../lib/strings';
import { UNKNOWN } from '../../constants';

const colorMap = {
  lightGreen: '#509038',
  darkGreen: '#325e34',
  lightRed: '#CA2C09',
  darkRed: '#651605',
  lightGrey: '#A9A9A9',
  darkGrey: '#566573',
};

type ColorMap = {
  header: string;
  body: string;
};

type StatusToColorMap = {
  [key: string]: ColorMap;
};

export const statusToColorMap: StatusToColorMap = {
  Active: { header: colorMap.lightGreen, body: colorMap.darkGreen },
  Landed: { header: colorMap.lightGreen, body: colorMap.darkGreen },
  Scheduled: { header: colorMap.lightGreen, body: colorMap.darkGreen },
  Diverted: { header: colorMap.lightRed, body: colorMap.darkRed },
  Cancelled: { header: colorMap.lightRed, body: colorMap.darkRed },
  Redirected: { header: colorMap.lightRed, body: colorMap.darkRed },
  'Not Operational': { header: colorMap.lightRed, body: colorMap.darkRed },
  Unknown: { header: colorMap.lightGrey, body: colorMap.darkGrey },
};

interface StatusProps {
  status?: string;
  divertedAirportDisplayCode?: string;
}

const StatusBar = styled.div<StatusProps>`
  color: white;
  display: block;
  text-align: center;
  font-size: 15pt;
  margin: 15px 0 5px 0;
  line-height: 20pt;
  padding: 5px 30px;
  width: auto;
  font-weight: bold;
  background-color: ${props =>
    statusToColorMap[props.status ?? UNKNOWN]?.header ?? colorMap.lightGrey};
`;

/**
 * ColorStatus component to display the status with appropriate color.
 * @param {StatusProps} props - The props for the component.
 * @returns {JSX.Element} The ColorStatus component.
 */
export const ColorStatus: React.FC<StatusProps> = ({
  status,
  divertedAirportDisplayCode,
}) => {
  const statusTitleCase = toTitleCase(status ?? '');
  const potentialDivertedAirportCodeDisplay = divertedAirportDisplayCode
    ? ` to ${divertedAirportDisplayCode}`
    : '';

  const statusDisplay = `${statusTitleCase}${potentialDivertedAirportCodeDisplay}`;

  return <StatusBar status={statusTitleCase}>{statusDisplay}</StatusBar>;
};

/**
 * Gets the color theme for a given status.
 * @param {string} status - The status to get the color theme for.
 * @returns {ColorMap} The color theme for the status.
 */
export const getStatusTheme = (status: string): ColorMap => {
  const statusDisplay = toTitleCase(status);

  return (
    statusToColorMap[statusDisplay] || { header: '#444444', body: '#666666' }
  );
};